import { useId, useState } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

export interface ToolFAQItem {
    question: string
    answer: string
}

interface ToolFAQSectionProps {
    heading: string
    faqs: ToolFAQItem[]
}

/**
 * Same visual pattern as FAQPage's accordion (collapsed answers still sit in
 * the DOM, just clipped via max-h-0 — the prerender snapshot and the
 * separate useFAQSchema JSON-LD both carry the full text regardless of
 * open/closed state, so nothing here depends on a user actually clicking).
 */
export function ToolFAQSection({ heading, faqs }: ToolFAQSectionProps) {
    const [openIndex, setOpenIndex] = useState<number | null>(null)
    const id = useId()

    if (faqs.length === 0) return null

    return (
        <section className="max-w-2xl mx-auto mt-12">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{heading}</h2>
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                {faqs.map((faq, index) => {
                    const isOpen = openIndex === index
                    return (
                        <div key={index} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                            <h3 className="contents">
                                <button
                                    type="button"
                                    onClick={() => setOpenIndex(isOpen ? null : index)}
                                    className="w-full py-4 px-5 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                                    aria-expanded={isOpen}
                                    aria-controls={`${id}-${index}`}
                                >
                                    <span data-faq-question className="text-sm font-medium text-gray-900 dark:text-white pr-4">
                                        {faq.question}
                                    </span>
                                    {isOpen ? (
                                        <ChevronUpIcon className="h-4 w-4 text-primary-500 flex-shrink-0" />
                                    ) : (
                                        <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                    )}
                                </button>
                            </h3>
                            {/* Collapsed answers stay in the DOM for the prerender
                                snapshot, but are hidden from assistive tech so what a
                                screen reader hears matches aria-expanded. */}
                            <div
                                id={`${id}-${index}`}
                                aria-hidden={!isOpen}
                                className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                                    }`}
                            >
                                <p data-faq-answer className="px-5 pb-4 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                    {faq.answer}
                                </p>
                            </div>
                        </div>
                    )
                })}
            </div>
        </section>
    )
}

export default ToolFAQSection
